import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, finalize, map,  switchMap, tap } from 'rxjs/operators';
import { IResponse } from 'src/app/core/interfaces/response.interface';
import { IDocModel } from 'src/app/core/models/doc.model';

import { ErrorService } from 'src/app/services/error.service';
import { ToastService } from 'src/app/services/toast.service';
import { environment } from 'src/environments/environment';
import * as DocActions from '../actions/doc.actions';
import { AppState } from '../app.reducer';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class DocEffects {
  constructor(
    public actions$: Actions,
    public http: HttpClient,
    private errorService: ErrorService,
    private store: Store<AppState>,
    private toastService: ToastService,
    private translate: TranslateService,
  ) { }
  payloadReload;


  searchDocs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocActions.searchDocs),
      switchMap(({ payload }) => {
        const body = payload.query || {};

        // set loading = true
        this.store.dispatch(DocActions.docLoading({ payload: { loading: true } }));

        return this.http
          .post<IResponse<IDocModel[]>>(environment.apiUrl + '/searchdocs', body)
          .pipe(
            // προσθέτουμε ένα tap για log
            tap(response => {
            }),
            // σε επιτυχία
            switchMap(response => {
              this.store.dispatch(
                DocActions.docLoading({ payload: { loading: false } })
              );
              return of(
                DocActions.searchDocsSuccess({
                  payload: {
                    docs: response.data,
                    totalCount: response.totalCount
                  }
                })
              );
            }),
            // σε αποτυχία
            catchError(err => {
              console.error('❌ [searchDocs$] error from API:', err);
              return of(
                DocActions.searchDocsFail({
                  payload: { error: this.errorService.mapError(err) }
                })
              );
            })
          );
      })
    )
  );


  getDocs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocActions.getDocs),
      switchMap(({ payload }) => {
        let query = {};
        if (payload?.query) {
          query = {
            ...payload.query,
            filters: JSON.stringify(payload.query.filters),
          };
        }
        this.store.dispatch(
          DocActions.docLoading({ payload: { loading: true } })
        );

        return this.http
          .get<IResponse<IDocModel[]>>(environment.apiUrl + '/docs', {
            params: { ...query },
          })
          .pipe(
            switchMap((responseData) => {
              this.store.dispatch(
                DocActions.docLoading({ payload: { loading: false } })
              );
              return of(
                DocActions.getDocsSuccess({
                  payload: {
                    docs: responseData.data,
                    totalCount: responseData.totalCount,
                  },
                })
              );
            }),
            catchError((error) => {
              return of(
                DocActions.docFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  getDoc$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocActions.getDocById),
      switchMap(({ payload }) => {
        this.store.dispatch(
          DocActions.docLoading({ payload: { loading: true } })
        );
        return this.http
          .get<any>(`${environment.apiUrl}/docs/${payload.id}`)
          .pipe(
            switchMap((responseData) => {

              this.store.dispatch(
                DocActions.getDocByIdSuccess({
                  payload: {
                    doc: responseData.data,
                  },
                })
              );
              return of(
                DocActions.docLoading({ payload: { loading: false } })
              );
            }),
            catchError((error) =>
              of(
                DocActions.docFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              )
            )
          );
      })
    )
  );


  updateDoc$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocActions.updateDoc),
      switchMap(({ payload }) => {
        this.store.dispatch(
          DocActions.docLoading({ payload: { loading: true } })
        );

        return this.http
          .put<any>(environment.apiUrl + '/docs/' + payload.doc.id, payload.doc)

          .pipe(
            switchMap(() => {
              this.store.dispatch(DocActions.docUpdated());
              return of(DocActions.docLoading({ payload: { loading: false } }));
            }),
            catchError((error) => {
              if (
                environment.optimisticLockErrors.includes(error.error?.name)
              ) {
                return of(
                  DocActions.docOptimisticLockError({
                    payload: {
                      optimisticLockError: this.errorService.mapError(error),
                    },
                  })
                );
              } else {
                return of(
                  DocActions.docFail({
                    payload: { error: this.errorService.mapError(error) },
                  })
                );
              }
            })
          );
      })
    )
  );

  mergeAndSaveAttachments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocActions.mergeAndSaveAttachments),
      switchMap((data) => {
        return this.http
          .post<any>(environment.apiUrl + '/attachments/merge-upload', {
            filename: data.payload.fileName,
            foldername: data.payload.folderName,
            filesize: data.payload.fileSize,
            doc_id: data.payload.doc_id,
            mimetype: data.payload.mimetype,
            unique_upload_id: data.payload.unique_upload_id,
          })
          .pipe(
            switchMap((res) => {
              return of(DocActions.fileUploaded({ payload: { file: res } }));
            }),
            catchError((error) => {
              return of(
                DocActions.docFileFail({
                  payload: { fileError: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  FileUploadedSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocActions.fileUploaded),
        tap((data) => {
          const translatedLabelIdle = this.translate.instant('PROP.SUCCESSFUL_FILE_UPLOAD');

          this.toastService.showToast(translatedLabelIdle);
        })
      ),
    { dispatch: false }
  );

  downloadFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocActions.downloadFile),
      switchMap((data) => {
        this.store.dispatch(
          DocActions.docLoading({
            payload: { loading: true },
          })
        );
        return this.http
          .get(
            environment.apiUrl +
            '/attachments' +
            '/getfile',
            {
              params: {
                file_id: data.payload.file_id.toString(),
                filename: data.payload.filename.toString(),
              },
              responseType: 'blob',
            }
          )
          .pipe(
            switchMap((blob) => {
              this.store.dispatch(DocActions.downloadFileSuccess());
              const fileURL = window.URL.createObjectURL(blob);
              window.open(fileURL);

              return of(
                DocActions.docLoading({
                  payload: { loading: false },
                })
              );
            }),
            catchError((error) => {
              this.store.dispatch(
                DocActions.docLoading({ payload: { loading: false } })
              );
              if (error.error instanceof Blob) {
                return of(
                  DocActions.docFileErrorMsgFromBlobResponse({
                    payload: { errorMsgBlob: error.error },
                  })
                );
              } else {
                return of(
                  DocActions.docFileFail({
                    payload: { fileError: this.errorService.mapError(error) },
                  })
                );
              }
            })
          );
      })
    )
  );

  removeDoc$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocActions.removeFile),
      switchMap(({ payload }) => {
        return this.http
          .delete<any>(
            environment.apiUrl +
            '/attachments/' +
            payload.doc_id +
            '/file/' +
            payload.file_id
          )
          .pipe(
            switchMap((data) => {
              this.store.dispatch(
                DocActions.fileRemoved({ payload: { removedFile: data } })
              );
              return of(DocActions.docLoading({ payload: { loading: false } }));
            }),
            catchError((error) => {
              if (
                environment.optimisticLockErrors.includes(error.error?.name)
              ) {
                return of(
                  DocActions.docOptimisticLockError({
                    payload: {
                      optimisticLockError: this.errorService.mapError(error),
                    },
                  })
                );
              } else {
                return of(
                  DocActions.docFail({
                    payload: { error: this.errorService.mapError(error) },
                  })
                );
              }
            })
          );
      })
    )
  );


  deleteDoc$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocActions.deleteDoc),
      switchMap(({ payload }) => {
        this.store.dispatch(
          DocActions.docLoading({ payload: { loading: true } })
        );

        return this.http
          .delete<any>(environment.apiUrl + '/deleteDocs/' + payload.id)
          .pipe(
            switchMap(() => {
              this.store.dispatch(DocActions.docDeleted());
              return of(DocActions.docLoading({ payload: { loading: false } }));
            }),
            catchError((error) => {
              if (
                environment.optimisticLockErrors.includes(error.error?.name)
              ) {
                return of(
                  DocActions.docOptimisticLockError({
                    payload: {
                      optimisticLockError: this.errorService.mapError(error),
                    },
                  })
                );
              } else {
                return of(
                  DocActions.docFail({
                    payload: { error: this.errorService.mapError(error) },
                  })
                );
              }
            })
          );
      })
    )
  );

  createDoc$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocActions.createDoc),
      switchMap(({ payload }) => {
        this.store.dispatch(DocActions.docLoading({ payload: { loading: true } }));
        return this.http.post<any>(`${environment.apiUrl}/createdocs`, {
          doc: payload.doc,
          id: payload.id,
          rightId: payload.rightId,
        }).pipe(
          map(responseData => {
            return DocActions.docCreated({ doc: responseData });
          }),
          catchError((error) => {
            if (environment.optimisticLockErrors.includes(error.error?.name)) {
              return of(
                DocActions.docOptimisticLockError({
                  payload: {
                    optimisticLockError: this.errorService.mapError(error),
                  },
                })
              );
            } else {
              return of(
                DocActions.docFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            }
          }),
          finalize(() => {
            this.store.dispatch(DocActions.docLoading({ payload: { loading: false } }));
          })
        );
      })
    )
  );

}
